/**
 * Loads customers through the injected API adapter.
 */
class CustomerRepository {
  /**
   * @param {object} apiAdapter - API client with a `getCustomers` method (same contract as the app API module).
   */
  constructor(
    apiAdapter = null
  ) {
    if (!apiAdapter) {
      throw new Error('apiAdapter is required');
    }

    this.apiAdapter = apiAdapter;
  }

  /**
   * Fetches customers by ID. IDs are deduplicated; requests are split into chunks of 100
   * and run in parallel. Empty input skips the network and returns an empty result.
   *
   * @async
   * @param {Array<string|number>} [ids=[]] - Customer IDs to load (falsy entries are ignored)
   * @returns {Promise<{ items: object[], total: number }>} Merged result in the same shape as `getCustomers` responses (`items` concatenated, `total` equals `items.length`).
   */
  async findByIds(ids = []) {
    const uniqueIds = [...new Set((ids || []).filter(Boolean))]
    if (!uniqueIds.length) {
      return { items: [], total: 0 }
    }

    const batchSize = 100
    const responses = await Promise.all(
      Array.from({ length: Math.ceil(uniqueIds.length / batchSize) }, (_, i) => {
        const chunk = uniqueIds.slice(i * batchSize, (i + 1) * batchSize)
        return this.apiAdapter.getCustomers({
          id: chunk.join(','),
          count: chunk.length
        })
      })
    )

    const items = responses.flatMap(r => r.items || [])
    return { items, total: items.length }
  }

  /**
   * Fetches customers matching the given first and last name via `getCustomers`.
   * At most 100 matches are returned (`count: 100`).
   * When both names are missing or blank after trim, skips the network and returns an empty result.
   * `firstName` may be an empty string when only `lastName` is used for search.
   * Blank names are omitted from the `getCustomers` payload.
   *
   * @async
   * @param {string} [firstName=''] - Given name to search (sent as `first_name`)
   * @param {string} [lastName=''] - Family name to search (sent as `last_name`)
   * @returns {Promise<{
   *   items: Array<{
   *     id: string,
   *     name: string,
   *     email: string,
   *     email_opted_in: boolean
   *   }>,
   *   total: number
   * }>} Result in the same shape as `getCustomers` responses.
   */
  async findByName(firstName = '', lastName = '') {
    const normalizedFirstName = typeof firstName === 'string' ? firstName.trim() : ''
    const normalizedLastName = typeof lastName === 'string' ? lastName.trim() : ''

    if (!normalizedFirstName && !normalizedLastName) {
      return { items: [], total: 0 }
    }

    return await this.apiAdapter.getCustomers({
      ...(normalizedFirstName && { first_name: normalizedFirstName }),
      ...(normalizedLastName && { last_name: normalizedLastName }),
      count: 100
    })
  }
}

export default CustomerRepository;